import AddIcon from '@mui/icons-material/Add';
import { Box, IconButton } from '@mui/material';
import dayjs from 'dayjs';
import React from 'react';
import { ApplicantProfileData } from '../../../../pages/rec/applicant/ApplicantProfile';
import api from '../../../../services/httpClient';
import { SETTINGS_ENDPOINTS } from '../../../../services/endpoints/settings.endpoints';
import { SYSTEM_ENDPOINTS } from '../../../../services/endpoints/system.endpoints';
import { formatDate } from '../../../../utils/date';
import { AsyncAutocomplete, Option } from '../../../common/AsyncAutocomplete';
import DatePickerField from '../../../common/DatePickerField';
import EditIcon from '../../../common/icons/EditIcon';
import { usePermissions } from '../../../../context/PermissionContext';
import { API_HEADERS } from '../../../../constants/apiHeaders';
import { PERMISSION_IDS } from '../../../../constants/permissions';
import { toText } from '../../../../utils/common';

interface ApplicantProfileQualificationCardProps {
  isEditMode?: boolean;
  values: ApplicantProfileData;
  setIsEditMode?: (isEditMode: boolean) => void;
  setValues: (values: ApplicantProfileData) => void;
}

interface QualificationCardProps {
  index: number;
  values: ApplicantProfileData;
  setIsEditMode?: (isEditMode: boolean) => void;
  setValues: (values: ApplicantProfileData) => void;
  education: { isEdit?: boolean } & ApplicantProfileData['educations'][number];
}

const QualificationCard: React.FC<QualificationCardProps> = ({ education, setIsEditMode, setValues, values, index }) => {
  const { hasPermission } = usePermissions();

  const handleLoadQualifications = async (query: string) => {
    const response = await api.get<{ data: { id: number; name: string }[] }>(
      SYSTEM_ENDPOINTS.qualifications,
      {
        headers: { ...API_HEADERS.SETTINGS_QUALIFICATIONS_VACANCY_ADD },
      }
    );
    return response.data.data.map((item) => ({
      value: item.id.toString(),
      label: item.name,
    }));
  };

  const handleLoadInstitutes = async (query: string) => {
    const response = await api.get<{ data: { id: number; name: string }[] }>(
      SETTINGS_ENDPOINTS.institutes,
      {
        headers: { ...API_HEADERS.APPLICANT_PROFILE_INSTITUTES_LOOKUP },
      }
    );
    return response.data.data.map((item) => ({
      value: item.id.toString(),
      label: item.name,
    }));
  };

  return (
    <Box className="view-sub-section">
      {!education.isEdit && hasPermission(PERMISSION_IDS.APPLICANT_PROFILE_EDIT) && (
        <IconButton
          onClick={() => {
            setValues({ ...values, educations: values.educations.map((education, i: number) => i === index ? { ...education, isEdit: true } : education) });
            setIsEditMode?.(true);
          }}
          className="view-edit-icon"
        >
          <EditIcon />
        </IconButton>
      )}
      <Box className="view-row-box">
        <Box className="view-column-box">
          <div className="view-label-title">
            Institute/ University Name *
          </div>
          {education.isEdit ? <AsyncAutocomplete
            className="view-column-edit-box"
            loadOptions={handleLoadInstitutes}
            value={education.institute ? { value: education.instituteId.toString(), label: toText(education.institute) } : null}
            onChange={(value: Option | null) => setValues({ ...values, educations: values.educations.map((education, i: number) => i === index ? { ...education, instituteId: value ? Number(value.value) : 0, institute: value ? value.label : '' } : education) })}
          /> : <div className="view-value-title">{toText(education.institute) || 'N/A'}</div>}
        </Box>

        <Box className="view-column-box">
          <div className="view-label-title">
            Qualification *
          </div>
          {education.isEdit ? <AsyncAutocomplete
            className="view-column-edit-box"
            loadOptions={handleLoadQualifications}
            value={education.degree ? { value: toText(education.degree), label: toText(education.degree) } : null}
            onChange={(value: Option | null) => setValues({ ...values, educations: values.educations.map((education, i: number) => i === index ? { ...education, degree: value ? value.label : '' } : education) })}
          /> : <div className="view-value-title">{toText(education.degree) || 'N/A'}</div>}
        </Box>

        <Box className="view-column-box">
          <div className="view-label-title">
            Duration
          </div>
          {education.isEdit ? (
            <Box className="view-column-edit-box" sx={{ display: 'flex', gap: 1 }}>
              <DatePickerField
                value={education.from ? dayjs(education.from) : null}
                onChange={(value: string | null) => setValues({ ...values, educations: values.educations.map((education, i: number) => i === index ? { ...education, from: value ? value : '' } : education) })}
              />
              <DatePickerField
                value={education.to ? dayjs(education.to) : null}
                onChange={(value: string | null) => setValues({ ...values, educations: values.educations.map((education, i: number) => i === index ? { ...education, to: value ? value : '' } : education) })}
              />
            </Box>
          ) : <div className="view-value-title">
            {(() => {
              const from = education.from ? formatDate(education.from) : '';
              const to = education.to ? formatDate(education.to) : '';
              if (!from && !to) return 'N/A';
              return `${from} - ${to}`;
            })()}
          </div>}
        </Box>

        {/* <Box className="view-column-box" /> */}
      </Box>
    </Box>
  );

}

const ApplicantProfileQualificationCard: React.FC<ApplicantProfileQualificationCardProps> = ({ isEditMode, setIsEditMode, setValues, values }) => {
  const { hasPermission } = usePermissions();

  return (
    <Box className="view-section">
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <div className="view-section-title">Qualifications</div>
        {hasPermission(PERMISSION_IDS.APPLICANT_PROFILE_EDIT) && (
          <IconButton
            className="edit-section-add-button"
            onClick={() => {
              setIsEditMode?.(true);
              setValues({
                ...values,
                educations: [...values.educations, {
                  instituteId: 0,
                  institute: '',
                  degree: '',
                  from: '',
                  to: '',
                  isEdit: true,
                }],
              });
            }}
          >
            <AddIcon />
          </IconButton>
        )}
      </Box>

      {values.educations?.length > 0 ? values.educations.map((education, index: number) => (
        <QualificationCard
          key={index}
          index={index}
          values={values}
          setValues={setValues}
          education={education}
          setIsEditMode={setIsEditMode}
        />
      )): <div className="view-value-title">N/A</div>}
    </Box>
  );
};

export default ApplicantProfileQualificationCard; 